import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  onRatingChange?: (rating: number) => void;
  className?: string;
  showLabel?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'md',
  interactive = false,
  onRatingChange,
  className,
  showLabel = false,
}) => {
  const [hoverRating, setHoverRating] = React.useState<number>(0);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-4 w-4';
      case 'lg':
        return 'h-6 w-6';
      default:
        return 'h-5 w-5';
    }
  };

  const handleStarClick = (starIndex: number) => {
    if (interactive && onRatingChange) {
      onRatingChange(starIndex + 1);
    }
  };

  const handleStarHover = (starIndex: number) => {
    if (interactive) {
      setHoverRating(starIndex + 1);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };

  const getStarFill = (starIndex: number): string => {
    const targetRating = interactive && hoverRating > 0 ? hoverRating : rating;
    
    if (starIndex < Math.floor(targetRating)) {
      return '#FBBF24'; // Full star
    } else if (starIndex < targetRating) {
      return '#FBBF24'; // Partial star (for now, we'll treat as full)
    } else {
      return '#D1D5DB'; // Empty star
    }
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <div 
        className="flex gap-1"
        onMouseLeave={handleMouseLeave}
      >
        {Array.from({ length: maxRating }, (_, index) => (
          <Star
            key={index}
            className={cn(
              getSizeClasses(),
              interactive && 'cursor-pointer hover:scale-110 transition-transform',
              'fill-current'
            )}
            style={{ color: getStarFill(index) }}
            onClick={() => handleStarClick(index)}
            onMouseEnter={() => handleStarHover(index)}
          />
        ))}
      </div>
      {showLabel && (
        <span className="text-sm text-muted-foreground ml-1">
          {interactive && hoverRating > 0 ? hoverRating : rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export { StarRating }; 
